import { useMemo } from 'react'
import { LogEntry } from '../types/shard'

// NOTE: This hook used to multiply hardcoded per-persona token estimates by
// Anthropic opus/sonnet catalog prices to produce an *invented* dollar figure.
// That contradicted Phase 5a routing — most personas route through LiteLLM to
// Azure OpenAI (gpt-5.4 family), not Anthropic — so the dollars were fictional.
//
// The authoritative dollar figure now comes from the LiteLLM proxy via
// `GET /api/projects/:name/spend` (see `useProjectSpend` + `ProjectSpendCard`).
// This hook is reduced to what the activity log can honestly tell us: how many
// Ralph-loop iterations each persona ran (parsed from DONE events). Loop counts
// are real; dollars are not derived here anymore.

interface AgentLoopRecord {
  persona: string
  taskId: string
  loops: number
}

export interface PersonaLoops {
  persona: string
  loops: number
}

export interface ActivityMetrics {
  /** Total Ralph-loop iterations across all DONE events. */
  totalLoops: number
  /** Loop counts per persona, descending. */
  byPersona: PersonaLoops[]
}

// Parse a log message to extract persona and loop data from DONE events
// Message format: "[Persona] DONE task TASK-XXX (status: XXX, loops: N)"
function parseDoneEvent(log: LogEntry): AgentLoopRecord | null {
  if (log.eventType !== 'DONE') return null

  const msg = log.message
  // Extract persona from [Persona]
  const personaMatch = msg.match(/^\[(\w+)\]/)
  if (!personaMatch) return null
  const persona = personaMatch[1]!.toLowerCase()

  // Extract task ID
  const taskMatch = msg.match(/task ((?:TASK|SPEC)-\d+)/)
  if (!taskMatch) return null
  const taskId = taskMatch[1]!

  // Extract loop count
  const loopMatch = msg.match(/loops:\s*(\d+)/)
  const loops = loopMatch ? parseInt(loopMatch[1]!, 10) : 1

  return { persona, taskId, loops }
}

export function useActivityCost(logs: LogEntry[]): ActivityMetrics {
  return useMemo(() => {
    const personaAgg = new Map<string, number>()
    let totalLoops = 0

    for (const log of logs) {
      const record = parseDoneEvent(log)
      if (!record) continue
      totalLoops += record.loops
      personaAgg.set(record.persona, (personaAgg.get(record.persona) || 0) + record.loops)
    }

    return {
      totalLoops,
      byPersona: Array.from(personaAgg.entries())
        .map(([persona, loops]) => ({ persona, loops }))
        .sort((a, b) => b.loops - a.loops),
    }
  }, [logs])
}
